import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';

/**
 * Paste handler. Two refinements over CodeMirror's default:
 *
 *   1. text/plain only — drop the rich-HTML clipboard payload from
 *      Word / Google Docs / etc., so we land canonical Markdown
 *      (or whatever the user actually copied).
 *   2. Truncate at maxLength, with a callback so the host can
 *      surface a toast.
 */
export function pastePolicy(opts: {
  maxLength?: number;
  onTruncate?: (info: { dropped: number }) => void;
}): Extension {
  return EditorView.domEventHandlers({
    paste(event, view) {
      const data = event.clipboardData;
      if (!data) return false;
      const text = data.getData('text/plain');
      if (!text) return false;

      event.preventDefault();

      const { from, to } = view.state.selection.main;
      const max = opts.maxLength ?? Number.POSITIVE_INFINITY;
      const docLenAfterRemoval = view.state.doc.length - (to - from);
      const remainingRoom = Math.max(0, max - docLenAfterRemoval);
      const insert = text.length > remainingRoom ? text.slice(0, remainingRoom) : text;
      const dropped = text.length - insert.length;

      view.dispatch({
        changes: { from, to, insert },
        selection: { anchor: from + insert.length },
      });
      if (dropped > 0) opts.onTruncate?.({ dropped });
      return true;
    },
  });
}
